import { Component, EventEmitter, HostBinding, Input, OnDestroy, Output, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { DataStorageService } from '../../services/data-storage.service';
import { Claim, PetInsuranceData, VetChatSummaryDto } from '../../models';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

interface CombinedItem {
  id: string;
  title: string;
  type: 'chat' | 'claim';
  date: Date;
  originalItem: VetChatSummaryDto | Claim;
}

@Component({
  selector: 'cpf-chat-history',
  templateUrl: './chat-history.component.html',
  styleUrls: ['./chat-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ChatHistoryComponent implements OnDestroy {
  @Input() @HostBinding('class.isDesktop') isDesktop = false;

  @Output() chatClicked = new EventEmitter<void>();
  @Output() itemDetailsClicked = new EventEmitter<any>();

  public petData?: PetInsuranceData;

  public combinedItems: CombinedItem[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private dataStorageService: DataStorageService,
    private translate: TranslateService
  ) {
    this.petData = this.dataStorageService.selectedData;
    this.setCombinedData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigateToChat(): void {
    if (this.chatClicked.observed) {
      this.chatClicked.emit();
      return;
    }
    this.router.navigate(['calingo', 'telemedicine', 'chat']);
  }

  setCombinedData() {
    const combinedItems: CombinedItem[] = [];

    if (this.dataStorageService.selectedData) {
      this.dataStorageService.chatSummaries.filter(chat => chat.tiername === this.dataStorageService.selectedData!.pet_name).forEach(chat => {
        combinedItems.push({
          id: chat.id,
          title: chat.medical_category || this.translate.instant('chat-claim-details.fallbacks.general-consultation'),
          type: 'chat',
          date: new Date(chat.created_at),
          originalItem: chat
        });
      });
    }

    if (this.dataStorageService.selectedData && this.dataStorageService.selectedData.claims) {
      this.dataStorageService.selectedData.claims.forEach(claim => {
        // First try find claim for same date
        const existingClaim = combinedItems.find(item => item.type === 'claim' && item.date.getTime() === new Date(claim.claim_date).getTime());

        if (existingClaim) {
          existingClaim.title += `, ${claim.cause || this.translate.instant('chat-claim-details.fallbacks.unknown-type')}`;
        } else {
          combinedItems.push({
            id: claim.identifier,
            title: claim.cause || this.translate.instant('chat-claim-details.fallbacks.unknown-type'),
            type: 'claim',
            date: new Date(claim.claim_date),
            originalItem: claim
          });
        }
      });
    }

    // Sort by date in descending order (newest first)
    this.combinedItems = combinedItems.sort((a, b) =>
      b.date.getTime() - a.date.getTime()
    );
  }

  openItem(item: CombinedItem) {
    const petData = this.dataStorageService.selectedData;
    const detailItem = {
      id: item.id,
      title: item.title,
      type: item.type,
      petName: petData?.pet_name || 'Unbekannt',
      originalItem: item.originalItem
    };

    if (this.itemDetailsClicked.observed) {
      this.itemDetailsClicked.emit(detailItem);
      return;
    }

    // Navigate to details page with URL parameters
    this.router.navigate(['calingo', 'telemedicine', 'details', item.type, item.id]);
  }
}
